import { Currency } from '@kodiak-finance/sdk-core'
import BGT_ABI from 'abis/BGT.json'
import { BGT_ADDRESSES } from 'constants/addresses'
import { useWriteContract } from 'hooks/useTransaction'
import useCurrencyBalance from 'lib/hooks/useCurrencyBalance'
import tryParseCurrencyAmount from 'lib/utils/tryParseCurrencyAmount'
import { useCallback, useMemo } from 'react'
import { useTransactionAdder } from 'state/transactions/hooks'
import { TransactionType } from 'state/transactions/types'
import { isUserRejection } from 'utils/userRejection'
import { Address, isAddress } from 'viem'
import { useSimulateContract } from 'wagmi'

import { REFETCH_POLICY_FAST } from '../config/RefetchPolicies'
import { useConnection } from './useConnection'

export enum BGTRedeemType {
  NOT_APPLICABLE,
  REDEEM,
}

enum BGTRedeemInputError {
  NO_ERROR,
  ENTER_BGT_AMOUNT,
  INSUFFICIENT_BGT_BALANCE,
}

const NOT_APPLICABLE = { redeemType: BGTRedeemType.NOT_APPLICABLE }

export function BGTRedeemErrorText({ bgtRedeemInputError }: { bgtRedeemInputError: BGTRedeemInputError }) {
  switch (bgtRedeemInputError) {
    case BGTRedeemInputError.NO_ERROR:
      return null
    case BGTRedeemInputError.ENTER_BGT_AMOUNT:
      return <>Enter BGT amount</>
    case BGTRedeemInputError.INSUFFICIENT_BGT_BALANCE:
      return <>Insufficient BGT balance</>
  }
}

export default function useBGTRedeemCallback(
  inputCurrency: Currency | undefined | null,
  outputCurrency: Currency | undefined | null,
  typedValue: string | undefined,
  recipient?: string | null
): {
  redeemType: BGTRedeemType
  execute?: undefined | (() => Promise<string>)
  inputError?: BGTRedeemInputError
  isPending?: boolean
} {
  const { chainId, account } = useConnection()
  const balance = useCurrencyBalance(account ?? undefined, inputCurrency ?? undefined, REFETCH_POLICY_FAST)
  const addTransaction = useTransactionAdder()

  const inputAmount = useMemo(
    () => tryParseCurrencyAmount(typedValue, inputCurrency ?? undefined),
    [inputCurrency, typedValue]
  )

  // Determine if this is a BGT redeem operation
  const bgtAddress = chainId ? BGT_ADDRESSES[chainId] : undefined
  const isBGTInput =
    bgtAddress && inputCurrency?.isToken && inputCurrency.address.toLowerCase() === bgtAddress.toLowerCase()
  const isBeraOutput = outputCurrency?.isNative
  const isRedeemOperation = isBGTInput && isBeraOutput

  const hasInputAmount = Boolean(inputAmount?.greaterThan('0'))
  const sufficientBalance = inputAmount && balance && !balance.lessThan(inputAmount)
  const redeemTo = useMemo(() => (recipient && isAddress(recipient) ? recipient : account), [recipient, account])

  // Simulate redeem transaction
  const { data: redeemSimulation } = useSimulateContract({
    abi: BGT_ABI,
    address: bgtAddress as Address | undefined,
    functionName: 'redeem',
    args: redeemTo && inputAmount ? [redeemTo as Address, BigInt(inputAmount.quotient.toString())] : undefined,
    chainId,
    query: {
      enabled: Boolean(isRedeemOperation && hasInputAmount && sufficientBalance && bgtAddress && redeemTo),
    },
  })

  const { writeContractAsync, isPending: isWritePending } = useWriteContract()

  const execute = useCallback(async () => {
    if (!chainId || !inputAmount || !redeemTo || !bgtAddress) {
      throw new Error('Missing dependencies')
    }

    if (!redeemSimulation?.request) {
      throw new Error('Transaction simulation failed')
    }

    try {
      const hash = await writeContractAsync(redeemSimulation.request)

      addTransaction({ hash } as any, {
        type: TransactionType.REDEEM,
        currencyAmountRaw: inputAmount?.quotient.toString(),
        chainId,
      })

      return hash
    } catch (error: any) {
      // Handle user rejection separately from actual errors
      if (isUserRejection(error)) {
        throw new Error('User rejected transaction')
      }

      console.error('BGT redeem failed:', error)
      throw error
    }
  }, [chainId, inputAmount, redeemTo, bgtAddress, redeemSimulation, writeContractAsync, addTransaction])

  return useMemo(() => {
    if (!chainId || !inputCurrency || !outputCurrency || !bgtAddress) return NOT_APPLICABLE

    if (!isRedeemOperation) return NOT_APPLICABLE

    return {
      redeemType: BGTRedeemType.REDEEM,
      isPending: isWritePending,
      execute: sufficientBalance && inputAmount ? execute : undefined,
      inputError: sufficientBalance
        ? undefined
        : hasInputAmount
        ? BGTRedeemInputError.INSUFFICIENT_BGT_BALANCE
        : BGTRedeemInputError.ENTER_BGT_AMOUNT,
    }
  }, [
    chainId,
    inputCurrency,
    outputCurrency,
    bgtAddress,
    isRedeemOperation,
    isWritePending,
    sufficientBalance,
    inputAmount,
    hasInputAmount,
    execute,
  ])
}
